export const PATIENT_REQUEST_OPTIONS = [
  { value: "vip_room", text: "חדר פרטי" },
  { value: "request_kosher", text: "כשרות" },
  { value: "request_diet", text: "דיאטה" },
  { value: "request_task_type", text: "כללי" }
];

/**
 * Temporary: hide category pickers and omit `category` from the API payload.
 * Description is still collected and sent. Flip to true to restore categories.
 */
export const PATIENT_REQUEST_CATEGORIES_ENABLED = false;

export const createEmptyPatientRequests = () => ({ types: [], notes: "" });

// Every patient request arrives as a task of this type, with the category in
// the description. The same key doubles as the general request option value.
const REQUEST_TASK_TYPE = "request_task_type";
const EMPTY_DESCRIPTION = "ללא פירוט";
const OPTIONAL_NOTES_TYPE = "vip_room";

const getOptionByValue = value =>
  PATIENT_REQUEST_OPTIONS.find(option => option.value === value);

const getOptionByLabel = label =>
  PATIENT_REQUEST_OPTIONS.find(option => option.text === label);

/**
 * Notes are optional only for private room. Any other selected type requires
 * a detail text before the request can be submitted.
 * While categories are hidden, notes stay optional.
 */
export const isPatientRequestNotesRequired = (types = []) =>
  PATIENT_REQUEST_CATEGORIES_ENABLED &&
  types.some(type => type !== OPTIONAL_NOTES_TYPE);

export const getPatientRequestNotesLabel = (types = []) =>
  isPatientRequestNotesRequired(types)
    ? "פירוט הבקשה*"
    : "פירוט הבקשה (לא חובה)";

export const getPatientRequestNotesPlaceholder = (types = []) =>
  isPatientRequestNotesRequired(types)
    ? "אנא הוסיפו פירוט עבור צוות השירות בבית החולים לשם הטיפול בבקשה"
    : "ניתן להוסיף פירוט עבור צוות השירות בבית החולים לשם הטיפול בבקשה";

/**
 * A request task holds both parts in its description, as "Category: detail".
 * Anything that does not start with a known category is treated as general.
 */
const parseTaskDescription = description => {
  const raw = (description || "").trim();
  const separatorIndex = raw.indexOf(":");
  const option =
    separatorIndex === -1
      ? null
      : getOptionByLabel(raw.slice(0, separatorIndex).trim());

  if (!option) return { type: REQUEST_TASK_TYPE, notes: raw };

  const detail = raw.slice(separatorIndex + 1).trim();
  return {
    type: option.value,
    notes: detail === EMPTY_DESCRIPTION ? "" : detail
  };
};

// Tasks arrive on the surgery, on the info response root and on the orders.
const collectTasks = (surgeryInfo, surgeryOrders) => [
  ...(surgeryInfo?.message?.surgery?.tasks || []),
  ...(surgeryInfo?.message?.tasks || []),
  ...(surgeryOrders?.data?.tasks || []),
  ...(surgeryOrders?.data?.orders || []).flatMap(order => order.tasks || [])
];

/**
 * Builds the form model out of the tasks of the surgery info and orders
 * responses.
 */
export const parsePatientRequests = (surgeryInfo, surgeryOrders) => {
  const parsedTasks = collectTasks(surgeryInfo, surgeryOrders)
    .filter(task => task.type === REQUEST_TASK_TYPE)
    .map(task => ({
      ...parseTaskDescription(task.description),
      rawDescription: (task.description || "").trim()
    }));

  const requests = parsedTasks.reduce((acc, { type, notes }) => {
    if (!acc.types.includes(type)) acc.types.push(type);
    // The form holds a single detail shared by all requests
    if (!acc.notes) acc.notes = notes;
    return acc;
  }, createEmptyPatientRequests());

  if (!PATIENT_REQUEST_CATEGORIES_ENABLED) {
    const items = parsedTasks
      .map(task => ({ description: task.rawDescription }))
      .filter(item => item.description);

    return {
      types: [],
      notes: items[0]?.description || "",
      items
    };
  }

  return requests;
};

export const getPatientRequestTypes = (labels = []) =>
  PATIENT_REQUEST_OPTIONS.filter(option => labels.includes(option.text)).map(
    option => option.value
  );

export const getPatientRequestSummary = (requests, sentTypes = []) => {
  if (!PATIENT_REQUEST_CATEGORIES_ENABLED) {
    if (Array.isArray(requests?.items) && requests.items.length) {
      return requests.items;
    }

    const description = requests?.notes?.trim();
    if (!description) return [];
    return [{ description }];
  }

  const description = requests?.notes?.trim() || EMPTY_DESCRIPTION;

  return (requests?.types || [])
    .filter(type => !sentTypes.includes(type))
    .map(getOptionByValue)
    .filter(Boolean)
    .map(option => ({ category: option.text, description }));
};

/**
 * Requests already sent are skipped, since the endpoint only creates tasks.
 * While categories are hidden, only description is sent (no category field).
 */
export const buildPatientRequestPayloads = (surgeryId, requests, sentTypes) => {
  if (!surgeryId) return [];

  if (!PATIENT_REQUEST_CATEGORIES_ENABLED) {
    const description = requests?.notes?.trim();
    if (!description) return [];

    return [
      {
        description,
        surgery_id: String(surgeryId),
        kind: "request"
      }
    ];
  }

  return getPatientRequestSummary(requests, sentTypes).map(request => ({
    ...request,
    surgery_id: String(surgeryId),
    kind: "request"
  }));
};
